import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ConvexProvider, ConvexReactClient } from "convex/react";

// Use environment variable or fallback for development
const convexUrl = process.env.REACT_APP_CONVEX_URL || "https://fiery-rabbit-322.convex.cloud";

// Create a Convex client with authentication
const convex = new ConvexReactClient(convexUrl, {
  // Add additional configuration for improved stability
  unsavedChangesWarning: false // Disable warning on page unload
});

// Helper function to ensure auth is applied correctly
const safeSetAuth = (tokenOrProvider: string | (() => Promise<string>)) => {
  try {
    // Clear any previous auth first to avoid conflicts
    convex.clearAuth();
    
    // Set the auth token with proper error handling
    if (typeof tokenOrProvider === 'string') {
      const token = tokenOrProvider;
      // Validate token format - it should be a non-empty string
      if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invalid token format');
      }
      
      // Set auth using a function that returns the token
      convex.setAuth(async () => token);
      
      // Store last token for debugging
      (window as any).__lastAuthToken = token.substring(0, 10) + '...';
    } else {
      // Set auth using the provided function
      convex.setAuth(tokenOrProvider);
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

// Setup Convex auth on window for AuthContext to use
window.convex = {
  setAuth: safeSetAuth,
  clearAuth: () => {
    try {
      convex.clearAuth();
      delete (window as any).__lastAuthToken;
      return true;
    } catch (error) {
      return false;
    }
  }
};

// Add a listener for Convex connection state changes
const connectionStatusListener = () => {
  // Only log connection changes in development mode
  if (process.env.NODE_ENV === 'development') {
    // Log only on error or after reconnect
    const state = convex.connectionState();
    if (!state.isWebSocketConnected || state.connectionRetries > 0) {
      console.log("Convex connection state:", state);
    }
  }
};

// Check connection state less frequently (30 seconds instead of 5)
const intervalId = setInterval(connectionStatusListener, 30000);

// Cleanup on window unload
window.addEventListener('beforeunload', () => {
  clearInterval(intervalId);
});

// Check for existing session token and apply it immediately
const existingToken = localStorage.getItem('sessionToken');
if (existingToken) {
  console.log("Found existing session token, applying immediately");
  safeSetAuth(existingToken);
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ConvexProvider client={convex}>
      <App />
    </ConvexProvider>
  </React.StrictMode>
);

reportWebVitals(); 